<script lang="ts">
  import Loading from '../../../etc/loader.svelte';
  import RepositoryFactory, { CONTRACT } from '../../../repositories/RepositoryFactory';
  import type { GetContractResult } from '../../../repositories/contract';
  import { loginUser } from '../../../components/auth/LoginUser';
  import { getContext } from 'svelte';
  import { differenceInCalendarMonths } from 'date-fns';

  const { close }: any = getContext('simple-modal');

  const terminateReasons = [
    { code: 'QUALITY', name: '品質' },
    { code: 'FEE', name: '料金' },
    { code: 'SUPPORT', name: 'サポート' },
    { code: 'OTHER', name: 'その他' }
  ];
  let terminateReasonCode: 'QUALITY' | 'FEE' | 'SUPPORT' | 'OTHER' = 'QUALITY';
  let terminateReasonDetail: string = '';

  let pageStatus: 'TOP' | 'INPUT' | 'CONFIRM' | 'COMPLETE' = 'TOP';

  let promise = initialize();
  let contract: GetContractResult;
  let terminateDate: string;
  let displayPenalty = false;

  // 違約金サービス一覧
  const PENALTY_SERVICE_CODE: any = [
    { code: 'SO_ROUTER_UNLIMITED', term: 24 },
    { code: 'EA_ROUTER_UNLIMITED_3696', term: 24 },
    { code: 'EA_ROUTER_UNLIMITED_2600', term: 24 },
    { code: 'EA_ROUTER_UNLIMITED_4196', term: 24 },
    { code: 'EA_ROUTER_UNLIMITED_4380', term: 36 },
    { code: 'EA_ROUTER_UNLIMITED_2980', term: 36 },
    { code: 'EA_ROUTER_UNLIMITED_4880', term: 36 }
  ];

  async function initialize() {
    const contractRepository = RepositoryFactory[CONTRACT];
    await contractRepository
      .getContract()
      .then((res) => {
        if (!res) {
          throw new Error();
        }
        contract = res;

        // 契約開始月〜解約日（現在年月）の月数を算出
        let diffMonths = differenceInCalendarMonths(new Date(), new Date(contract.contractStartDate));

        // 対象サービスの解約可能サイクル期間（月）を取得
        let penaltyTerm = PENALTY_SERVICE_CODE.find((value) => contract.serviceCode === value.code).term;

        // 違約金が発生しないかチェック
        displayPenalty = diffMonths / penaltyTerm === 0 || diffMonths % penaltyTerm > 0;
      })
      .catch((error) => {
        displayErrorAlert(error.response.status);
      });
    return contract;
  }

  async function requestTerminate() {
    const contractRepository = RepositoryFactory[CONTRACT];
    await contractRepository
      .terminateContract({
        terminateTypeCode: 'APPLIED',
        terminateReasonCode: terminateReasonCode,
        terminateReasonDetail: terminateReasonDetail
      })
      .then((res) => {
        terminateDate = res.terminateDate;
        transisionWithdrawalComplete();
      })
      .catch((error) => {
        if (error.response.status === 403) {
          alert('セッションが切れました。再度ログインしてください。');
          loginUser.logout();
          location.reload();
        } else {
          alert('エラーが発生したため、解約できませんでした。');
        }
      });
  }

  function displayErrorAlert(status: number) {
    if (status === 403) {
      alert('セッションが切れました。再度ログインしてください。');
      loginUser.logout();
      location.reload();
    } else {
      alert('エラーが発生しました。再度やり直してください。');
    }
  }

  function handleWithdrawalTop() {
    pageStatus = 'TOP';
    terminateReasonCode = 'QUALITY';
    terminateReasonDetail = '';
  }

  function handleWithdrawalInput() {
    pageStatus = 'INPUT';
  }

  function handleWithdrawalComfirm() {
    pageStatus = 'CONFIRM';
  }

  function handleWithdrawalComplete() {
    requestTerminate();
  }

  function handleWithdrawalCompleteClose() {
    close();
    location.reload();
  }

  function transisionWithdrawalComplete() {
    pageStatus = 'COMPLETE';
  }
</script>

{#await promise}
  <Loading />
{:then contract}
  {#if pageStatus === 'TOP'}<!-- 解約 TOP-->
    <div class="shadow rounded bg-white border-t-2 border-green-400 mt-5">
      <div class="flex-1 m-5">
        <div class="inline-flex justify-items-center items-center min-w-full">
          <span class="mb:font-sm">ご契約いただいている以下内容を解約します。</span>
        </div>
      </div>
      <div class="flex-1 m-5">
        <div class="mb:font-sm">{contract.serviceName}</div>
        {#if displayPenalty}
          <div class="mb:font-sm mt-3">契約解除料：10,450円</div>
        {/if}
      </div>
      <div class="flex-1 m-5">
        <div class="inline-flex justify-items-center items-center min-w-full">
          <span class="mb:font-sm">注意事項等をご確認いただき、「手続きへ」を押してください。</span>
        </div>
      </div>

      <div class="flex-1 mb-10">
        <div class="shadow rounded-lg bg-gray-200 border-1 m-5 p-4">
          <div class="flex flex-col">
            <div class="flex-1">
              <div class="justify-self-start w-full inline-block">
                <ul>
                  <li>注意事項</li>
                  <li>・「解約する」ボタンを押した後のキャンセルはできません</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex-1">
        <div class="flex flex-col justify-items-center items-center">
          <div class="flex-1 m-5 flex flex-col md:flex-row justify-items-center items-center">
            <button
              on:click|once={handleWithdrawalInput}
              class="button-primary mb-5"
              disabled={contract.contractStatusCode === 'TERMINATED'}
            >
              手続きへ
            </button>
          </div>
        </div>
      </div>
    </div>
  {:else if pageStatus === 'INPUT'}<!-- 解約 入力-->
    <div class="shadow rounded bg-white border-t-2 border-green-400 mt-5">
      <div class="m-5">
        <span class="form-label-title mb-1 required">解約理由</span>
        <div class="flex flex-col w-full md:flex-row md:space-x-0 lg:space-x-4">
          {#each terminateReasons as item}
            <label class="flex items-center">
              <input type="radio" name="terminateReason" value={item.code} bind:group={terminateReasonCode} />
              <span class="text-sm ml-1">{item.name}</span>
            </label>
          {/each}
        </div>
      </div>
      <div class="m-5">
        <div class="form-label-title mb-1">解約理由詳細</div>
        <textarea
          class="w-full p-2 border focus:ring-0 focus:ring-blue-200 focus:border-transparent text-sm"
          bind:value={terminateReasonDetail}
        />
      </div>
      <div class="flex-1">
        <div class="flex flex-col justify-items-center items-center">
          <div class="flex-1 m-5 flex flex-col md:flex-row justify-items-center items-center">
            <button on:click|once={handleWithdrawalTop} class="button-secondary mb-5"> 戻る </button>
            <button
              class="button-primary md:ml-5 mb-5"
              disabled={contract.contractStatusCode === 'TERMINATED'}
              on:click={handleWithdrawalComfirm}
            >
              確認する
            </button>
          </div>
        </div>
      </div>
    </div>
  {:else if pageStatus === 'CONFIRM'}<!-- 解約 確認-->
    <div class="shadow rounded bg-white border-t-2 border-green-400 mt-5">
      <div class="flex-1 m-5">
        <div class="inline-flex justify-items-center items-center min-w-full">
          <span class="mb:font-sm">この内容で本当に解約しますか？</span>
        </div>
      </div>
      <div class="flex-1 m-5">
        <div class="inline-flex justify-items-center items-center min-w-full">
          <span class="mb:font-sm">{contract.serviceName}</span>
        </div>
      </div>

      <div class="m-5">
        <span class="form-label-title">解約理由</span>
        <div class="flex space-x-4 w-full mt-1">
          <div class="label-value">
            {terminateReasons.filter((reason) => reason.code === terminateReasonCode)[0].name}
          </div>
        </div>
      </div>
      <div class="m-5">
        <div class="form-label-title">解約理由詳細</div>
        <div class="label-value">{terminateReasonDetail ?? '-'}</div>
      </div>
      <div class="flex-1 mt-8">
        <div class="flex flex-col justify-items-center items-center">
          <div class="flex-1 m-5 flex flex-col md:flex-row justify-items-center items-center">
            <button on:click|once={handleWithdrawalInput} class="button-secondary mb-5"> 戻る </button>
            <button on:click|once={handleWithdrawalComplete} class="button-primary md:ml-5 mb-5"> 解約する </button>
          </div>
        </div>
      </div>
    </div>
  {:else if pageStatus === 'COMPLETE'}<!-- 解約 完了-->
    <div class="shadow rounded bg-white border-t-2 border-green-400">
      <div class="flex-1 m-5">
        <div class="mb:font-sm">ご利用ありがとうございました。</div>
        <div class="mb:font-sm mt-3">解約日：{terminateDate}</div>
      </div>
      <div class="flex-1 m-5">
        <div class="inline-flex justify-items-center items-center min-w-full">
          <span class="mb:font-sm">マイページは解約後、一定期間過ぎると利用できなくなるためご注意ください。</span>
        </div>
      </div>

      <div class="flex flex-col justify-items-center items-center">
        <div class="flex-1 m-5">
          <button on:click|once={handleWithdrawalCompleteClose} class="button-primary"> 閉じる </button>
        </div>
      </div>
    </div>
  {/if}
{/await}

<style lang="postcss">
  .required:after {
    content: ' *';
    color: red;
  }
  .button-primary {
    @apply bg-green-400 rounded hover:bg-green-500 text-sm font-semibold text-white focus:outline-none px-10 py-2;
  }
  .button-primary:disabled {
    @apply opacity-30 cursor-default;
  }
  .button-secondary {
    @apply bg-gray-300 rounded hover:bg-gray-400 text-sm font-semibold text-gray-500 focus:outline-none px-10 py-2;
  }
  .button-secondary:disabled {
    @apply opacity-30 cursor-default;
  }
  .form-label-title {
    @apply text-sm font-semibold text-gray-700;
  }
  .label-value {
    @apply text-sm text-gray-700 leading-5;
  }
</style>
